import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { AccessDenied } from "@/components/AccessDenied";
import {
  meetsPermissionRequirement,
  type PermissionRequirement,
} from "@/constants/permissions";
import { useAuthStore } from "@/stores/authStore";

type PermissionRouteProps = {
  children: ReactNode;
  deniedTo?: string;
  requirement: PermissionRequirement;
};

type AuthenticatedRouteProps = {
  children: ReactNode;
};

export const AuthenticatedRoute = ({ children }: AuthenticatedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export const PermissionRoute = ({
  children,
  deniedTo,
  requirement,
}: PermissionRouteProps) => {
  const { hasAnyPermission, hasPermission, isAuthenticated, isLoading } =
    useAuthStore();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (
    !meetsPermissionRequirement(requirement, {
      hasPermission,
      hasAnyPermission,
    })
  ) {
    return deniedTo ? <Navigate to={deniedTo} replace /> : <AccessDenied />;
  }

  return children;
};
